import React from "react";
import { resolveImageUrl } from "../../services/api";
import { DIETARY_TAG_OPTIONS } from "./dietaryTags";
import { useT } from "../../i18n/useT";

// AM/OM are machine drafts (2026-06) — pending native review
const STRINGS = {
  en: {
    allItems: "All items ({n})",
    name: "Name",
    category: "Category",
    price: "Price",
    available: "Available",
    chefPick: "Chef's pick",
    uploading: "Uploading...",
    image: "Image",
    edit: "Edit",
    del: "Delete",
    soldOut: "Sold out",
    inStock: "In stock",
  },
  am: {
    allItems: "ሁሉም ዕቃዎች ({n})",
    name: "ስም",
    category: "ምድብ",
    price: "ዋጋ",
    available: "ይገኛል",
    chefPick: "የሼፍ ምርጫ",
    uploading: "በመስቀል ላይ...",
    image: "ምስል",
    edit: "አርትዕ",
    del: "አጥፋ",
    soldOut: "አልቋል",
    inStock: "አለ",
  },
  om: {
    allItems: "Meeshaalee hunda ({n})",
    name: "Maqaa",
    category: "Ramaddii",
    price: "Gatii",
    available: "Ni argama",
    chefPick: "Filannoo Cheef",
    uploading: "Olkaa'aa jira...",
    image: "Suuraa",
    edit: "Sirreessi",
    del: "Balleessi",
    soldOut: "Dhumeera",
    inStock: "Jira",
  },
};

// "All items" table card. Purely presentational — item data and the
// upload / edit / delete handlers live in ManagerMenuPage.
export default function MenuItemTable({ items, uploadingFor, onUpload, onEdit, onDelete, onToggleAvailability }) {
  const { lang } = useT();
  const t = (k) => (STRINGS[lang] && STRINGS[lang][k]) || STRINGS.en[k] || k;
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>{t("allItems").replace("{n}", items.length)}</h3>
      <table className="data">
        <thead>
          <tr>
            <th></th>
            <th>{t("name")}</th>
            <th>{t("category")}</th>
            <th>{t("price")}</th>
            <th>{t("available")}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.id} style={{ opacity: it.available ? 1 : 0.5 }}>
              <td style={{ width: 70 }}>
                {it.image_url ? (
                  <img
                    src={resolveImageUrl(it.image_url)}
                    alt=""
                    style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 8 }}
                  />
                ) : (
                  <div style={{
                    width: 56, height: 56, borderRadius: 8,
                    background: "#f0ece6", display: "flex",
                    alignItems: "center", justifyContent: "center",
                    color: "#b9b2a4",
                  }}>—</div>
                )}
              </td>
              <td>
                {it.is_chef_pick && (
                  <span
                    title={it.chef_pick_note || t("chefPick")}
                    style={{
                      color: "#a8892a", marginRight: 6, fontSize: "1.05rem",
                    }}
                    aria-label={t("chefPick")}
                  >
                    ⭐
                  </span>
                )}
                <b>{it.name}</b>
                {it.name_am && (
                  <span lang="am" className="muted" style={{ marginLeft: 6 }}>
                    ({it.name_am})
                  </span>
                )}
                {it.name_om && (
                  <span lang="om" className="muted" style={{ marginLeft: 6 }}>
                    [{it.name_om}]
                  </span>
                )}
                {it.name_sid && (
                  <span lang="sid" className="muted" style={{ marginLeft: 6 }}>
                    ⟨{it.name_sid}⟩
                  </span>
                )}
                {it.name_ti && (
                  <span lang="ti" className="muted" style={{ marginLeft: 6 }}>
                    ⟨{it.name_ti}⟩
                  </span>
                )}
                {it.name_nl && (
                  <span lang="nl" className="muted" style={{ marginLeft: 6 }}>
                    🇳🇱 {it.name_nl}
                  </span>
                )}
                {Array.isArray(it.dietary_tags) && it.dietary_tags.length > 0 && (
                  <div style={{ marginTop: 4, display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {it.dietary_tags.map((slug) => {
                      const opt = DIETARY_TAG_OPTIONS.find((o) => o.slug === slug);
                      if (!opt) return null;
                      return (
                        <span
                          key={slug}
                          style={{
                            background: opt.color,
                            color: "#fff",
                            fontSize: "0.7rem",
                            padding: "1px 7px",
                            borderRadius: 999,
                            fontWeight: 600,
                          }}
                        >
                          {opt.label}
                        </span>
                      );
                    })}
                  </div>
                )}
                {it.description && (
                  <div className="muted" style={{ fontSize: "0.85rem" }}>{it.description}</div>
                )}
              </td>
              <td className="muted">{it.category || "—"}</td>
              <td>{it.price.toFixed(2)}</td>
              <td>
                <button
                  type="button"
                  className={"btn small " + (it.available ? "secondary" : "danger")}
                  onClick={() => onToggleAvailability(it)}
                  title={it.available ? "Tap to mark sold out (hide from guests)" : "Tap to mark back in stock"}
                >
                  {it.available ? t("inStock") : t("soldOut")}
                </button>
              </td>
              <td style={{ whiteSpace: "nowrap" }}>
                <button className="btn secondary small" onClick={() => onUpload(it.id)}>
                  {uploadingFor === it.id ? t("uploading") : t("image")}
                </button>{" "}
                <button className="btn secondary small" onClick={() => onEdit(it)}>
                  {t("edit")}
                </button>{" "}
                <button className="btn danger small" onClick={() => onDelete(it.id)}>
                  {t("del")}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
